/**
 * Quick Start — the in-dashboard integration guide.
 *
 * Three lanes via a segmented control:
 *   1. Quick start   — the 4-step happy path (key → invoice → checkout → webhook)
 *   2. SDKs          — Node / Python / Rust / PHP, install + create + verify
 *   3. WooCommerce   — install the plugin, enable the gateway, get paid
 *   4. Widget        — online-store buy button (products + payment links) or a one-off pay button
 *
 * All code is copy-paste. Screenshots live in /public/guide/ and are served
 * from /guide/*.png. This page is read-only; it links out to /api-keys,
 * /webhooks and /pages for the actions themselves.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Btn } from "../components/ui";
import { Ic } from "../components/Icons";
import {
  ApiError,
  createInvoiceAdmin,
  getMyMerchant,
  verifySettlementSetup,
  createInvoiceWithKey,
  listInvoicesAdmin,
  mintApiKey,
  setWebhookEndpoint,
  simulateTestPayment,
  type Invoice,
} from "../lib/api";
import {
  getStoredApiKey,
  setStoredApiKey,
} from "../components/RawKeyModal";
import { highlight } from "../lib/highlight";
import { useT } from "../lib/i18n";
import { formatUsd } from "../lib/format";
import { getCheckoutKey, rotateCheckoutKey, setCheckoutOrigins, type CheckoutKey } from "../lib/checkout-key";
import {
  buyButtonSnippet,
  createPaymentLink,
  createProduct,
  listPaymentLinks,
  listProducts,
  priceLabel,
  setClientPrices,
  toCents,
  type PaymentLink,
  type Product,
} from "../lib/catalog";
import "./widget-guide.css";

type Lane = "quickstart" | "sdks" | "woocommerce" | "widget";
type Sdk = "node" | "python" | "rust" | "php";

// ─── Copyable code block ──────────────────────────────────────────────

function Code({ code, lang }: { code: string; lang?: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* clipboard unavailable — text is still selectable */
    }
  };
  return (
    <div className="qs-code">
      <div className="qs-code-top">
        <span className="qs-code-lang">{lang ?? "code"}</span>
        <button
          type="button"
          className={`qs-copy${done ? " copied" : ""}`}
          onClick={() => void copy()}
          aria-label="Copy code"
        >
          <Ic name={done ? "check" : "copy"} size={13} />
          {done ? "Copied" : "Copy"}
        </button>
      </div>
      <pre>
        <code
          dangerouslySetInnerHTML={{ __html: highlight(code, lang) }}
        />
      </pre>
    </div>
  );
}

// ─── Numbered step wrapper ────────────────────────────────────────────

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="qs-step">
      <div className="qs-step-rail">
        <div className="qs-step-num">{n}</div>
      </div>
      <div className="qs-step-body">
        <h3 className="qs-step-title">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Shot({
  src,
  alt,
  cap,
  onZoom,
}: {
  src: string;
  alt: string;
  cap: string;
  onZoom?: (src: string, alt: string) => void;
}) {
  return (
    <figure className="qs-shot">
      <button
        type="button"
        className="qs-shot-btn"
        onClick={() => onZoom?.(src, alt)}
        aria-label="Enlarge image"
      >
        <img src={src} alt={alt} loading="lazy" />
        <span className="qs-shot-zoom" aria-hidden="true">
          <Ic name="plus" size={15} />
        </span>
      </button>
      <figcaption>{cap}</figcaption>
    </figure>
  );
}

// A revealed secret (raw API key / signing secret) — shown once, copyable.
function Secret({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* selectable fallback */
    }
  };
  return (
    <div className="qs-secret">
      <code className="qs-secret-val">{value}</code>
      <button
        type="button"
        className={`qs-copy${done ? " copied" : ""}`}
        onClick={() => void copy()}
        aria-label="Copy secret"
      >
        <Ic name={done ? "check" : "copy"} size={13} />
        {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// Live / Test key-mode segmented control.
function ModeSeg({
  mode,
  onPick,
}: {
  mode: "live" | "test";
  onPick: (m: "live" | "test") => void;
}) {
  return (
    <div className="qs-modeseg" role="tablist" aria-label="Key mode">
      {(["live", "test"] as const).map((m) => (
        <button
          key={m}
          type="button"
          role="tab"
          aria-selected={mode === m}
          className={`qs-modeseg-btn${mode === m ? " active" : ""}`}
          onClick={() => onPick(m)}
        >
          {m === "live" ? "Live" : "Test"}
        </button>
      ))}
    </div>
  );
}

// ─── Code samples ─────────────────────────────────────────────────────

const createCurl = (key: string) => `curl https://shieldz.cash/api/v1/invoices \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount_usd_cents": 2500,
    "memo": "Order #1234",
    "customer_email": "buyer@example.com",
    "metadata": { "order_id": "1234" }
  }'`;

const CREATE_RES = `{
  "id": "R1uykKHSKVXc3Qb5x2T9",
  "object": "invoice",
  "amount_usd_cents": 2500,
  "status": "pending",
  "pay_url": "https://shieldz.cash/pay/R1uykKHSKVXc3Qb5x2T9",
  "expires_at": 1750000000000,
  "mode": "live"
}`;

const WEBHOOK_VERIFY = `import { createHmac, timingSafeEqual } from "node:crypto";

// Shieldz signs every webhook:  X-Shieldz-Signature: t=<unix>,v1=<hex>
// where hex = HMAC_SHA256(signing_secret, "<t>.<raw_request_body>").
function verifyShieldzWebhook(rawBody, header, secret) {
  const parts = header.split(",");
  const t = parts.find(p => p.startsWith("t="))?.slice(2);
  if (!t) return false;
  // Replay guard: reject events older than 5 minutes.
  if (Math.abs(Math.floor(Date.now() / 1000) - Number(t)) > 300) return false;

  const expected = createHmac("sha256", secret).update(t + "." + rawBody).digest("hex");
  const sigs = parts.filter(p => p.startsWith("v1=")).map(p => p.slice(3));
  return sigs.some(s => s.length === expected.length &&
    timingSafeEqual(Buffer.from(s), Buffer.from(expected)));
}

// IMPORTANT: verify against the RAW request body bytes, before JSON.parse.
// Dedupe on X-Shieldz-Delivery (at-least-once delivery).`;

type VerifyLang = "node" | "go" | "rust" | "php";

const VERIFY: Record<VerifyLang, { label: string; lang: string; code: string }> = {
  node: { label: "Node", lang: "javascript", code: WEBHOOK_VERIFY },
  go: {
    label: "Go",
    lang: "go",
    code: `import (
	"crypto/hmac"
	"crypto/sha256"
	"encoding/hex"
	"strconv"
	"strings"
	"time"
)

// X-Shieldz-Signature: t=<unix>,v1=<hex>  hex = HMAC_SHA256(secret, "<t>.<rawBody>")
func VerifyShieldzWebhook(rawBody, header, secret string) bool {
	var t, v1 string
	for _, p := range strings.Split(header, ",") {
		switch {
		case strings.HasPrefix(p, "t="):
			t = p[2:]
		case strings.HasPrefix(p, "v1="):
			v1 = p[3:]
		}
	}
	ts, err := strconv.ParseInt(t, 10, 64)
	if err != nil || time.Now().Unix()-ts > 300 || ts-time.Now().Unix() > 300 {
		return false // replay guard: reject events older than 5 minutes
	}
	mac := hmac.New(sha256.New, []byte(secret))
	mac.Write([]byte(t + "." + rawBody))
	expected := hex.EncodeToString(mac.Sum(nil))
	return hmac.Equal([]byte(v1), []byte(expected))
}`,
  },
  rust: {
    label: "Rust",
    lang: "rust",
    code: `use hmac::{Hmac, Mac};
use sha2::Sha256;

// X-Shieldz-Signature: t=<unix>,v1=<hex>  hex = HMAC_SHA256(secret, "<t>.<rawBody>")
fn verify_shieldz_webhook(raw_body: &str, header: &str, secret: &str) -> bool {
    let (mut t, mut v1) = ("", "");
    for p in header.split(',') {
        if let Some(x) = p.strip_prefix("t=") { t = x; }
        else if let Some(x) = p.strip_prefix("v1=") { v1 = x; }
    }
    // Replay guard: also check t is within 300s of your clock.
    let mut mac = Hmac::<Sha256>::new_from_slice(secret.as_bytes()).unwrap();
    mac.update(format!("{t}.{raw_body}").as_bytes());
    let expected = hex::encode(mac.finalize().into_bytes());
    // Constant-time compare (subtle::ConstantTimeEq) is preferred in production.
    v1 == expected
}`,
  },
  php: {
    label: "PHP",
    lang: "php",
    code: `<?php
// X-Shieldz-Signature: t=<unix>,v1=<hex>  hex = HMAC_SHA256(secret, "<t>.<rawBody>")
function verify_shieldz_webhook(string $rawBody, string $header, string $secret): bool {
    $t = null; $v1 = null;
    foreach (explode(',', $header) as $p) {
        if (str_starts_with($p, 't='))  $t  = substr($p, 2);
        if (str_starts_with($p, 'v1=')) $v1 = substr($p, 3);
    }
    // Replay guard: reject events older than 5 minutes.
    if ($t === null || abs(time() - (int) $t) > 300) return false;
    $expected = hash_hmac('sha256', $t . '.' . $rawBody, $secret);
    return $v1 !== null && hash_equals($expected, $v1);
}`,
  },
};

const SDK_META: Record<Sdk, { label: string; install: string; installLang: string }> = {
  node: { label: "Node", install: "npm install @shieldz/sdk", installLang: "bash" },
  python: { label: "Python", install: "pip install shieldz", installLang: "bash" },
  rust: { label: "Rust", install: "cargo add shieldz", installLang: "bash" },
  php: { label: "PHP", install: "composer require shieldz/shieldz", installLang: "bash" },
};

const SDK_CREATE: Record<Sdk, { code: string; lang: string }> = {
  node: {
    lang: "javascript",
    code: `import Shieldz from "@shieldz/sdk";

const shieldz = new Shieldz(process.env.SHIELDZ_API_KEY);

const invoice = await shieldz.invoices.create({
  amount_usd_cents: 5000,          // $50.00
  memo: "Order #1234",
  metadata: { order_id: "1234" },
});

// → send your customer to invoice.pay_url (the hosted checkout)
console.log(invoice.id, invoice.status, invoice.pay_url);`,
  },
  python: {
    lang: "python",
    code: `import os
from shieldz import Shieldz

shieldz = Shieldz(os.environ["SHIELDZ_API_KEY"])

invoice = shieldz.invoices.create(
    amount_usd_cents=5000,           # $50.00
    memo="Order #1234",
    metadata={"order_id": "1234"},
)

# → send your customer to invoice["pay_url"] (the hosted checkout)
print(invoice["id"], invoice["status"], invoice["pay_url"])`,
  },
  rust: {
    lang: "rust",
    code: `use shieldz::Shieldz;
use serde_json::json;

let shieldz = Shieldz::new(std::env::var("SHIELDZ_API_KEY")?);

let invoice = shieldz.invoices().create(json!({
    "amount_usd_cents": 5000,        // $50.00
    "memo": "Order #1234",
    "metadata": { "order_id": "1234" },
}))?;

// → send your customer to invoice["pay_url"] (the hosted checkout)
println!("{} {}", invoice["status"], invoice["pay_url"]);`,
  },
  php: {
    lang: "php",
    code: `use Shieldz\\Shieldz;

$shieldz = new Shieldz(getenv('SHIELDZ_API_KEY'));

$invoice = $shieldz->invoices->create([
    'amount_usd_cents' => 5000,        // $50.00
    'memo' => 'Order #1234',
    'metadata' => ['order_id' => '1234'],
]);

// send your customer to the hosted checkout
echo $invoice['pay_url'];`,
  },
};

const SDK_VERIFY: Record<Sdk, { code: string; lang: string }> = {
  node: {
    lang: "javascript",
    code: `import { constructEvent } from "@shieldz/sdk";

// Verify against the RAW request body, then act on it.
const event = constructEvent(rawBody, req.headers["x-shieldz-signature"], WHSEC);
if (event.type === "invoice.paid") fulfill(event.data.invoice);`,
  },
  python: {
    lang: "python",
    code: `from shieldz import construct_event, SignatureVerificationError

try:
    event = construct_event(
        request.get_data(),                    # raw bytes
        request.headers.get("X-Shieldz-Signature", ""),
        os.environ["SHIELDZ_WEBHOOK_SECRET"],
    )
except SignatureVerificationError:
    return "", 400

if event["type"] == "invoice.paid":
    ...  # fulfill — dedupe on X-Shieldz-Delivery (at-least-once)`,
  },
  rust: {
    lang: "rust",
    code: `use shieldz::{construct_event, VerifyOptions};

let event = construct_event(
    raw_body,                        // &[u8]
    signature_header,                // X-Shieldz-Signature
    &signing_secret,                 // whsec_…
    &VerifyOptions::default(),       // 300s tolerance
)?;

if event["type"] == "invoice.paid" {
    // fulfill — dedupe on X-Shieldz-Delivery (at-least-once)
}`,
  },
  php: {
    lang: "php",
    code: `use Shieldz\\Webhooks;
use Shieldz\\SignatureVerificationError;

$raw = file_get_contents('php://input');
$sig = $_SERVER['HTTP_X_SHIELDZ_SIGNATURE'] ?? '';

try {
    $event = Webhooks::constructEvent($raw, $sig, getenv('SHIELDZ_WEBHOOK_SECRET'));
} catch (SignatureVerificationError $e) {
    http_response_code(400);
    exit;
}

if ($event['type'] === 'invoice.paid') {
    // fulfill — dedupe on the X-Shieldz-Delivery header (at-least-once)
}`,
  },
};

const SDK_REPO: Record<Sdk, string> = {
  node: "https://github.com/ShieldZCash/shieldz-sdk",
  python: "https://github.com/ShieldZCash/shieldz-python",
  rust: "https://github.com/ShieldZCash/shieldz-rust",
  php: "https://github.com/ShieldZCash/shieldz-php",
};

// ─── Screen ───────────────────────────────────────────────────────────

export function QuickStart() {
  const t = useT();
  const [lane, setLane] = useState<Lane>("quickstart");
  const [sdk, setSdk] = useState<Sdk>("node");
  const [verifyLang, setVerifyLang] = useState<VerifyLang>("node");
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);

  // Interactive: mint a key + create a webhook without leaving the page.
  const [cachedKey, setCachedKey] = useState<string | null>(getStoredApiKey());
  const [mintedKey, setMintedKey] = useState<string | null>(null);
  const [keyMode, setKeyMode] = useState<"live" | "test">("live");
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyErr, setKeyErr] = useState<string | null>(null);

  const [hookUrl, setHookUrl] = useState("");
  const [hookSecret, setHookSecret] = useState<string | null>(null);
  const [hookBusy, setHookBusy] = useState(false);
  const [hookErr, setHookErr] = useState<string | null>(null);
  const [hookSaved, setHookSaved] = useState(false);

  const handleMint = async () => {
    setKeyBusy(true);
    setKeyErr(null);
    try {
      const k = await mintApiKey({ name: `quickstart-${keyMode}`, mode: keyMode });
      setStoredApiKey(k.raw_key);
      setCachedKey(k.raw_key);
      setMintedKey(k.raw_key);
    } catch (e) {
      setKeyErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setKeyBusy(false);
    }
  };

  const [tryBusy, setTryBusy] = useState(false);
  const [tryResult, setTryResult] = useState<Invoice | null>(null);
  const [tryErr, setTryErr] = useState<string | null>(null);

  const handleTryInvoice = async () => {
    const key = mintedKey ?? cachedKey ?? getStoredApiKey();
    if (!key) {
      setTryErr("Mint an API key in step 1 first.");
      return;
    }
    setTryBusy(true);
    setTryErr(null);
    try {
      const inv = await createInvoiceWithKey(key, {
        amount_usd_cents: 2500,
        memo: "Order #1234",
        customer_email: "buyer@example.com",
        metadata: { order_id: "1234" },
      });
      setTryResult(inv);
    } catch (e) {
      setTryErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setTryBusy(false);
    }
  };

  const [finBusy, setFinBusy] = useState(false);
  const [finOk, setFinOk] = useState<string | null>(null);
  const [finErr, setFinErr] = useState<string | null>(null);

  const handleFinalize = async () => {
    if (!tryResult) return;
    setFinBusy(true);
    setFinOk(null);
    setFinErr(null);
    try {
      const r = await simulateTestPayment(tryResult.id);
      setFinOk(r.message ?? "Payment simulated — invoice.paid webhook enqueued.");
      setTryResult({ ...tryResult, status: "paid", paid_at: Date.now() });
    } catch (e) {
      if (e instanceof ApiError) {
        // Friendlier copy for the two expected pre-conditions.
        if (e.code === "live_mode_not_simulatable") {
          setFinErr(
            "This is a live invoice — it settles on a real on-chain payment. Mint a test key (toggle Live → Test in step 1) and Try it again to simulate the webhook.",
          );
        } else if (e.code === "no_webhook_endpoint") {
          setFinErr(
            "Set a webhook endpoint in step 4 first — that's where the simulated invoice.paid event is delivered.",
          );
        } else {
          setFinErr(e.message);
        }
      } else {
        setFinErr(String(e));
      }
    } finally {
      setFinBusy(false);
    }
  };

  const handleCreateHook = async () => {
    const key = cachedKey ?? getStoredApiKey();
    if (!key) {
      setHookErr("Mint an API key in step 1 first.");
      return;
    }
    const url = hookUrl.trim();
    if (!url.startsWith("https://")) {
      setHookErr("Enter an https:// endpoint URL.");
      return;
    }
    setHookBusy(true);
    setHookErr(null);
    setHookSaved(false);
    try {
      const r = await setWebhookEndpoint(key, { url });
      setHookSaved(true);
      if (r.signing_secret) setHookSecret(r.signing_secret);
    } catch (e) {
      setHookErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setHookBusy(false);
    }
  };

  return (
    <div className="scroll">
      <div className="pad fade-in">
        <div className="qs-intro">
          <div className="qs-eyebrow">
            <Ic name="sparkles" size={14} /> {t("qs.eyebrow")}
          </div>
          <h2 className="qs-h2">{t("qs.h2")}</h2>
          <p className="qs-lead">{t("qs.lead")}</p>
          <p className="qs-lead" style={{ marginTop: 8 }}>
            <Link to="/pages" className="qs-link">
              {t("qs.nocode_cta")}
            </Link>
          </p>
        </div>

        <div className="qs-seg" role="tablist" aria-label="Integration path">
          {(
            [
              ["quickstart", "qs.tab.api", "zap"],
              ["sdks", "qs.tab.sdks", "code"],
              ["woocommerce", "qs.tab.woo", "wallet"],
              ["widget", "qs.tab.widget", "globe"],
            ] as [Lane, string, "zap" | "code" | "wallet" | "globe"][]
          ).map(([id, label, icon]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={lane === id}
              className={`qs-seg-btn${lane === id ? " active" : ""}`}
              onClick={() => setLane(id)}
            >
              <Ic name={icon} size={15} />
              {t(label)}
            </button>
          ))}
        </div>

        {lane === "quickstart" && (
          <div className="qs-lane">
            <Step n={1} title={t("qs.step.key")}>
              <p className="qs-p">
                Mint a live key right here — one click. Keep it server-side; it
                can create invoices on your account.
              </p>
              {mintedKey ? (
                <div className="qs-inline ok">
                  <div className="qs-inline-head">
                    <Ic name="check" size={15} /> Your new API key — copy it now,
                    it is shown only once
                  </div>
                  <Secret value={mintedKey} />
                  <p className="qs-note">
                    Cached for this browser session, so step 4 below can use it.
                    Manage or revoke keys on the{" "}
                    <Link to="/api-keys" className="qs-link">
                      API keys
                    </Link>{" "}
                    page.
                  </p>
                </div>
              ) : cachedKey ? (
                <div className="qs-inline ok">
                  <div className="qs-inline-head">
                    <Ic name="check" size={15} /> An API key is already cached for
                    this session
                  </div>
                  <div className="qs-actions">
                    <ModeSeg mode={keyMode} onPick={setKeyMode} />
                    <Btn
                      variant="secondary"
                      onClick={() => void handleMint()}
                      loading={keyBusy}
                    >
                      <Ic name="key" size={15} /> Mint {keyMode} key
                    </Btn>
                  </div>
                </div>
              ) : (
                <div className="qs-actions">
                  <ModeSeg mode={keyMode} onPick={setKeyMode} />
                  <Btn onClick={() => void handleMint()} loading={keyBusy}>
                    <Ic name="key" size={15} /> Mint {keyMode} API key
                  </Btn>
                  <Link to="/api-keys" className="qs-link">
                    Or manage keys manually →
                  </Link>
                </div>
              )}
              {keyMode === "test" && !mintedKey && (
                <p className="qs-note">
                  Test keys create <code className="mono">sk_test_…</code> invoices —
                  no real money, but webhooks fire for real, so you can run the full
                  create → finalize → webhook loop below.
                </p>
              )}
              {keyErr && (
                <p className="qs-note qs-err">
                  {keyErr} — you can also mint on the{" "}
                  <Link to="/api-keys" className="qs-link">
                    API keys
                  </Link>{" "}
                  page.
                </p>
              )}
              <p className="qs-note">
                <code className="mono">sk_live_…</code> for production,{" "}
                <code className="mono">sk_test_…</code> for test mode (simulated
                settlement, real webhooks).
              </p>
            </Step>

            <Step n={2} title={t("qs.step.invoice")}>
              <p className="qs-p">
                <code className="mono">POST /api/v1/invoices</code> with the
                amount in USD cents. You get back a{" "}
                <code className="mono">pay_url</code> — the hosted checkout.
              </p>
              {(mintedKey ?? cachedKey) && (
                <p className="qs-note qs-live-note">
                  <Ic name="check" size={13} /> Your live key is filled in below —
                  this <code className="mono">curl</code> runs as-is.
                </p>
              )}
              <Code
                code={createCurl(mintedKey ?? cachedKey ?? "sk_live_your_key")}
                lang="bash"
              />
              <div className="qs-tryrow">
                <Btn onClick={() => void handleTryInvoice()} loading={tryBusy}>
                  <Ic name="zap" size={15} /> {t("qs.try_it")}
                </Btn>
                {(() => {
                  const key = mintedKey ?? cachedKey ?? getStoredApiKey();
                  if (!key)
                    return (
                      <span className="qs-note">
                        Mint a key in step 1 to run this for real.
                      </span>
                    );
                  return key.startsWith("sk_test_") ? (
                    <span className="qs-note">
                      Creates a <b>test</b> $25.00 invoice — no real money, and you
                      can finalize it below.
                    </span>
                  ) : (
                    <span className="qs-note">
                      Creates a <b>real</b> $25.00 invoice on your account.
                    </span>
                  );
                })()}
              </div>
              {tryErr && <p className="qs-note qs-err">{tryErr}</p>}
              <div className="qs-sub">
                {tryResult ? "Response · live" : "Response"}
              </div>
              <Code
                code={
                  tryResult
                    ? JSON.stringify(tryResult, null, 2)
                    : CREATE_RES
                }
                lang="json"
              />
              {tryResult && (
                <>
                  <div className="qs-tryrow">
                    <a
                      className="qs-open"
                      href={tryResult.pay_url}
                      target="_blank"
                      rel="noopener"
                    >
                      <Ic name="ext" size={15} /> Open the checkout
                    </a>
                    {tryResult.status !== "paid" && (
                      <Btn
                        variant="secondary"
                        onClick={() => void handleFinalize()}
                        loading={finBusy}
                      >
                        <Ic name="check" size={15} /> Finalize payment
                      </Btn>
                    )}
                    <span className="qs-note">
                      Invoice <code className="mono">{tryResult.id}</code> ·{" "}
                      {tryResult.mode} mode ·{" "}
                      {tryResult.status === "paid" ? "paid ✓" : tryResult.status}
                    </span>
                  </div>
                  <p className="qs-note">
                    <b>Finalize payment</b> simulates settlement on a{" "}
                    <code className="mono">test</code> invoice — it marks it paid and
                    fires the real <code className="mono">invoice.paid</code> webhook,
                    so you can test your handler (step 4) end-to-end.
                  </p>
                  {finErr && <p className="qs-note qs-err">{finErr}</p>}
                  {finOk && (
                    <div className="qs-inline ok">
                      <div className="qs-inline-head">
                        <Ic name="check" size={15} /> Payment finalized
                      </div>
                      <p className="qs-note" style={{ marginTop: 0 }}>
                        {finOk} Watch it land on the{" "}
                        <Link to="/webhooks" className="qs-link">
                          Webhooks
                        </Link>{" "}
                        page (delivery log), or at your endpoint URL.
                      </p>
                    </div>
                  )}
                </>
              )}
            </Step>

            <Step n={3} title={t("qs.step.checkout")}>
              <p className="qs-p">
                Redirect to <code className="mono">pay_url</code>. The buyer pays
                any coin — BTC, ETH, USDC, SOL and more — and funds settle to
                your wallet as USDC (or your chosen asset). You never touch the
                keys.
              </p>
              <Shot
                onZoom={(s, a) => setZoom({ src: s, alt: a })}
                src="/guide/hosted-paypage.png"
                alt="Shieldz hosted checkout page showing a crypto payment with a QR code and pay-any-coin options"
                cap="The hosted checkout — buyers pay any coin, you settle to your wallet."
              />
            </Step>

            <Step n={4} title={t("qs.step.webhook")}>
              <p className="qs-p">
                Point Shieldz at your endpoint. When an invoice is paid, we POST
                a signed <code className="mono">invoice.paid</code> event and give
                you a <code className="mono">whsec_…</code> secret to verify it.
              </p>
              <div className="qs-inline">
                <div className="qs-inline-head">
                  <Ic name="link" size={15} /> Create a webhook endpoint
                </div>
                <div className="qs-hookrow">
                  <input
                    className="qs-input"
                    type="url"
                    inputMode="url"
                    placeholder="https://yourstore.com/webhooks/shieldz"
                    value={hookUrl}
                    onChange={(e) => setHookUrl(e.target.value)}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <Btn
                    onClick={() => void handleCreateHook()}
                    loading={hookBusy}
                    disabled={!hookUrl.trim()}
                  >
                    Create
                  </Btn>
                </div>
                {!cachedKey && !getStoredApiKey() && (
                  <p className="qs-note">
                    Mint an API key in step 1 first — the endpoint is saved
                    against it.
                  </p>
                )}
                {hookErr && <p className="qs-note qs-err">{hookErr}</p>}
                {hookSaved && (
                  <div className="qs-hookok">
                    <div className="qs-inline-head">
                      <Ic name="check" size={15} /> Endpoint saved
                      {hookSecret ? " — here is your signing secret" : ""}
                    </div>
                    {hookSecret ? (
                      <>
                        <Secret value={hookSecret} />
                        <p className="qs-note">
                          Store it as <code className="mono">SHIELDZ_WEBHOOK_SECRET</code>.
                          Shown once — rotate it anytime on the{" "}
                          <Link to="/webhooks" className="qs-link">
                            Webhooks
                          </Link>{" "}
                          page.
                        </p>
                      </>
                    ) : (
                      <p className="qs-note">
                        Your endpoint was updated. The signing secret is unchanged
                        — find it on the{" "}
                        <Link to="/webhooks" className="qs-link">
                          Webhooks
                        </Link>{" "}
                        page.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="qs-sub">Verify it — pick your language</div>
              <div className="qs-subtabs" role="tablist" aria-label="Verify language">
                {(Object.keys(VERIFY) as VerifyLang[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={verifyLang === k}
                    className={`qs-subtab${verifyLang === k ? " active" : ""}`}
                    onClick={() => setVerifyLang(k)}
                  >
                    {VERIFY[k].label}
                  </button>
                ))}
              </div>
              <Code code={VERIFY[verifyLang].code} lang={VERIFY[verifyLang].lang} />
              <p className="qs-note">
                Prefer a typed client with verification built in? See the{" "}
                <button
                  type="button"
                  className="qs-link qs-linkbtn"
                  onClick={() => setLane("sdks")}
                >
                  SDKs
                </button>{" "}
                tab. Running a store?{" "}
                <button
                  type="button"
                  className="qs-link qs-linkbtn"
                  onClick={() => setLane("woocommerce")}
                >
                  WooCommerce
                </button>{" "}
                takes zero code.
              </p>
            </Step>

            <div className="qs-more">
              <a
                href="https://shieldz.cash/docs"
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="ext" size={14} /> Full REST reference
              </a>
              <a
                href="https://shieldz.cash/reference"
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="ext" size={14} /> API explorer
              </a>
              <a
                href="https://shieldz.cash/openapi.json"
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="ext" size={14} /> OpenAPI spec
              </a>
            </div>
          </div>
        )}

        {lane === "sdks" && (
          <div className="qs-lane">
            <div className="qs-subtabs" role="tablist" aria-label="SDK language">
              {(Object.keys(SDK_META) as Sdk[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={sdk === k}
                  className={`qs-subtab${sdk === k ? " active" : ""}`}
                  onClick={() => setSdk(k)}
                >
                  {SDK_META[k].label}
                </button>
              ))}
            </div>

            <div className="qs-sub">1 · Install</div>
            <Code code={SDK_META[sdk].install} lang={SDK_META[sdk].installLang} />

            <div className="qs-sub">2 · Create an invoice</div>
            <Code code={SDK_CREATE[sdk].code} lang={SDK_CREATE[sdk].lang} />

            <div className="qs-sub">3 · Verify a webhook</div>
            <Code code={SDK_VERIFY[sdk].code} lang={SDK_VERIFY[sdk].lang} />

            <p className="qs-note">
              Every SDK does idempotent retries, auto-pagination, and
              constant-time signature verification. Get your key on the{" "}
              <Link to="/api-keys" className="qs-link">
                API keys
              </Link>{" "}
              page.
            </p>
            <div className="qs-more">
              <a
                href={SDK_REPO[sdk]}
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="code" size={14} /> {SDK_META[sdk].label} SDK source
              </a>
              <a
                href="https://shieldz.cash/sdks"
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="ext" size={14} /> All SDKs
              </a>
            </div>
          </div>
        )}

        {lane === "woocommerce" && (
          <div className="qs-lane">
            <p className="qs-p qs-lead-sm">{t("qs.woo.lead")}</p>

            <Step n={1} title={t("qs.woo.step1")}>
              <p className="qs-p">
                Download the plugin, then in WordPress go to{" "}
                <strong>Plugins → Add New → Upload Plugin</strong>, choose the
                zip, and activate.
              </p>
              <div className="qs-actions">
                <a
                  href="https://shieldz.cash/downloads/shieldz-crypto-payments.zip"
                  className="qs-dl"
                >
                  <Ic name="download" size={15} /> Download plugin (.zip)
                </a>
                <a
                  href="https://wordpress.org/plugins/search/shieldz/"
                  target="_blank"
                  rel="noopener"
                  className="qs-link"
                >
                  Or install from WordPress.org →
                </a>
              </div>
            </Step>

            <Step n={2} title={t("qs.woo.step2")}>
              <p className="qs-p">
                Go to <strong>WooCommerce → Settings → Payments</strong>, enable{" "}
                <strong>Shieldz — Crypto Payments</strong>, and open its
                settings. Paste your settlement <strong>wallet address</strong>{" "}
                (keyless mode) or an <strong>API key</strong> for full webhook
                reconciliation. Save.
              </p>
              <Shot
                onZoom={(s, a) => setZoom({ src: s, alt: a })}
                src="/guide/woo-gateway-settings.png"
                alt="WooCommerce payment settings with the Shieldz crypto gateway enabled and its configuration fields"
                cap="WooCommerce → Settings → Payments → Shieldz."
              />
            </Step>

            <Step n={3} title={t("qs.woo.step3")}>
              <p className="qs-p">
                Shieldz now appears as a payment option. The buyer selects it and
                places the order.
              </p>
              <Shot
                onZoom={(s, a) => setZoom({ src: s, alt: a })}
                src="/guide/blocks-checkout.png"
                alt="WooCommerce Blocks checkout showing Shieldz crypto as a selectable payment method"
                cap="Shieldz on the WooCommerce Blocks checkout."
              />
            </Step>

            <Step n={4} title={t("qs.woo.step4")}>
              <p className="qs-p">
                The buyer lands on the hosted Shieldz checkout, pays in any coin,
                and funds settle to your wallet. WooCommerce marks the order paid
                automatically — no manual reconciliation.
              </p>
              <div className="qs-shot-row">
                <Shot
                  onZoom={(s, a) => setZoom({ src: s, alt: a })}
                  src="/guide/hosted-paypage.png"
                  alt="Shieldz hosted checkout with a QR code for a crypto payment"
                  cap="Hosted checkout — pay any coin."
                />
                <Shot
                  onZoom={(s, a) => setZoom({ src: s, alt: a })}
                  src="/guide/order-received.png"
                  alt="WooCommerce order-received confirmation page after a successful Shieldz crypto payment"
                  cap="Order received — settled to your wallet."
                />
              </div>
            </Step>

            <div className="qs-more">
              <a
                href="https://shieldz.cash/accept-crypto-woocommerce"
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="ext" size={14} /> WooCommerce guide
              </a>
              <a
                href="https://shieldz.cash/docs"
                target="_blank"
                rel="noopener"
                className="qs-morelink"
              >
                <Ic name="ext" size={14} /> Docs
              </a>
            </div>
          </div>
        )}

        {lane === "widget" && <WidgetLane onWoo={() => setLane("woocommerce")} />}

        {zoom && (
          <div
            className="qs-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={zoom.alt}
            onClick={() => setZoom(null)}
          >
            <button
              type="button"
              className="qs-lightbox-x"
              onClick={() => setZoom(null)}
              aria-label="Close"
            >
              <Ic name="x" size={20} />
            </button>
            <img
              src={zoom.src}
              alt={zoom.alt}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Widget lane ──────────────────────────────────────────────────────
// Step by step, plain words: make a payment link, pick a look, paste two
// lines, try it live right here. The preview loads the real widget.js.

const WIDGET_SRC = "https://shieldz.cash/widget.js";

declare global {
  interface Window {
    Shieldz?: {
      scan: (root?: Element) => void;
      checkout: (opts: Record<string, unknown>) => Promise<{ invoice: string; payUrl: string }>;
    };
  }
}

/** Load widget.js once; resolves when window.Shieldz is ready. */
const loadWidget = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.Shieldz) return resolve();
    let el = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_SRC}"]`);
    if (!el) {
      el = document.createElement("script");
      el.src = WIDGET_SRC;
      el.defer = true;
      document.head.appendChild(el);
    }
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", () => reject(new Error("widget.js did not load")));
  });

/** The id the widget takes is the one in the payment link: shieldz.cash/pay/<id>. */
const linkId = (inv: Invoice): string => inv.pay_url.split("/pay/")[1]?.split(/[?#]/)[0] ?? inv.id;

const parseCents = (s: string): number | null => {
  const v = s.trim().replace(/^\$/, "");
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const cents = Math.round(parseFloat(v) * 100);
  return cents >= 100 && cents <= 10_000_000 ? cents : null;
};

type Look = "button" | "box";

function WidgetPreview({ id, look }: { id: string; look: Look }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadWidget()
      .then(() => { if (live && ref.current) window.Shieldz?.scan(ref.current); })
      .catch((e) => { if (live) setErr(String(e instanceof Error ? e.message : e)); });
    return () => { live = false; };
  }, [id, look]);
  if (err) return <p className="qs-note qs-err">The preview could not load ({err}). Your code still works on your site.</p>;
  return (
    <div className="wg-preview" ref={ref} key={`${look}:${id}`}>
      {look === "button"
        ? <button type="button" data-shieldz-pay={id}>Pay with crypto</button>
        : <div data-shieldz-inline={id} />}
    </div>
  );
}

function OneTimeFlow() {
  const t = useT();
  const [links, setLinks] = useState<Invoice[] | null>(null);
  const [picked, setPicked] = useState<Invoice | null>(null);
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [look, setLook] = useState<Look>("button");

  useEffect(() => {
    listInvoicesAdmin({ status: "pending", limit: 5 })
      .then((r) => setLinks(r.data))
      .catch(() => setLinks([]));
  }, []);

  const create = async () => {
    const cents = parseCents(amount);
    if (cents == null) { setErr("Type an amount between $1 and $100,000, like 25 or 9.99."); return; }
    setErr(null);
    setBusy(true);
    try {
      const inv = await createInvoiceAdmin({ amount_usd_cents: cents, memo: memo.trim() || null });
      setPicked(inv);
      setLinks((l) => [inv, ...(l ?? []).filter((x) => x.id !== inv.id)].slice(0, 5));
      setAmount("");
      setMemo("");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const id = picked ? linkId(picked) : "YOUR_PAYMENT_ID";
  const snippet = look === "button"
    ? `<script src="${WIDGET_SRC}" defer></script>\n<button data-shieldz-pay="${id}">Pay with crypto</button>`
    : `<script src="${WIDGET_SRC}" defer></script>\n<div data-shieldz-inline="${id}"></div>`;

  return (
    <>
      <Step n={1} title={t("qs.widget.step1")}>
        <p className="qs-p">
          The widget opens a payment link. Type the price and what it is for, then press
          <strong> Create</strong>.
        </p>
        <div className="qs-inline">
          <div className="wg-form">
            <label className="wg-field">
              <span>Price in USD</span>
              <input className="qs-input" inputMode="decimal" placeholder="25" value={amount}
                onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="wg-field wg-grow">
              <span>What is it for? (optional)</span>
              <input className="qs-input" placeholder="Logo design" value={memo} maxLength={140}
                onChange={(e) => setMemo(e.target.value)} />
            </label>
            <Btn onClick={() => void create()} loading={busy} disabled={busy}>
              <Ic name="plus" size={15} /> Create
            </Btn>
          </div>
          {err && <p className="qs-note qs-err">{err}</p>}
          {links && links.length > 0 && (
            <>
              <div className="qs-sub">Or use one you already made</div>
              <div className="wg-links">
                {links.map((inv) => (
                  <button key={inv.id} type="button"
                    className={`wg-link${picked?.id === inv.id ? " on" : ""}`}
                    onClick={() => setPicked(inv)}>
                    <strong>{formatUsd(inv.amount_usd_cents)}</strong>
                    <span>{inv.memo || "No description"}</span>
                    {picked?.id === inv.id && <Ic name="check" size={14} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {picked && (
          <p className="qs-note wg-ok">
            <Ic name="check" size={13} /> Using your {formatUsd(picked.amount_usd_cents)} payment link. Its ID is
            already in the code below.
          </p>
        )}
      </Step>

      <Step n={2} title={t("qs.widget.step2")}>
        <div className="wg-looks" role="radiogroup" aria-label="Widget style">
          <button type="button" role="radio" aria-checked={look === "button"}
            className={`wg-look${look === "button" ? " on" : ""}`} onClick={() => setLook("button")}>
            <span className="wg-look-art wg-art-btn"><i /></span>
            <strong>A pay button</strong>
            <span>The checkout opens in a small window on top of your page.</span>
          </button>
          <button type="button" role="radio" aria-checked={look === "box"}
            className={`wg-look${look === "box" ? " on" : ""}`} onClick={() => setLook("box")}>
            <span className="wg-look-art wg-art-box"><i /><i /><i /></span>
            <strong>A box on the page</strong>
            <span>The checkout sits right inside your page, always visible.</span>
          </button>
        </div>
      </Step>

      <Step n={3} title={t("qs.widget.step3")}>
        <p className="qs-p">
          Press <strong>Copy</strong>, then paste it into your website where you want the
          {look === "button" ? " button" : " box"} to show up.
        </p>
        {!picked && (
          <p className="qs-note">Make or pick a payment link in step 1 first, and its ID fills in here by itself.</p>
        )}
        <Code code={snippet} lang="html" />
        <div className="qs-sub">Where do I paste it?</div>
        <ul className="wg-where">
          <li><strong>WordPress:</strong> add a <em>Custom HTML</em> block.</li>
          <li><strong>Wix:</strong> Add, then <em>Embed code</em>, then <em>Embed HTML</em>.</li>
          <li><strong>Squarespace:</strong> add a <em>Code</em> block.</li>
          <li><strong>Shopify:</strong> add a <em>Custom Liquid</em> section.</li>
          <li><strong>Your own site:</strong> paste it into the page's HTML.</li>
        </ul>
      </Step>

      <Step n={4} title={t("qs.widget.step4")}>
        {picked ? (
          <>
            <p className="qs-p">
              This is exactly what your buyers will see. Go ahead and click it. Nothing is charged
              unless you actually send crypto.
            </p>
            <WidgetPreview id={id} look={look} />
          </>
        ) : (
          <p className="qs-p">Your live preview shows up here once you have a payment link from step 1.</p>
        )}
      </Step>

      <Step n={5} title={t("qs.widget.step5")}>
        <p className="qs-p">
          When someone pays, the money lands in your wallet and the payment shows up as paid in{" "}
          <Link to="/activity" className="qs-link">Activity</Link>. You will also get a notification if
          you turned them on.
        </p>
        <p className="qs-note">
          Want your website to react on its own, like unlocking a download? Set up a{" "}
          <Link to="/webhooks" className="qs-link">webhook</Link>. We send it the moment a payment is
          confirmed.
        </p>
      </Step>

      <div className="qs-inline wg-tip">
        <div className="qs-inline-head"><Ic name="info" size={15} /> Good to know</div>
        <p className="qs-note" style={{ marginTop: 0 }}>
          One payment link takes one payment. Selling to many people, or prices that change with the
          cart? Choose <strong>An online store</strong> at the top.
        </p>
      </div>
    </>
  );
}

// ─── Online store path ────────────────────────────────────────────────
// Stripe's model: prices live in Shieldz (Products), a payment link bundles
// what is sold, and the buy button on the store's page only names that link.
// The buyer's browser never sets the price. A JS cart can name price IDs
// with the shop key (opt-in), and a store with a server creates payments
// with its secret API key. Orders ship from Activity or the webhook.

type StoreHow = "button" | "js" | "server";
type ServerLang = "node" | "php" | "python";

const STORE_JS = (pk: string) => `<script src="${WIDGET_SRC}"
        data-key="${pk}" defer></script>
<script>
  // Run this when the buyer presses your checkout button. The page only says
  // what is in the cart; the prices come from your Products page.
  Shieldz.checkout({
    lineItems: [
      { price: "price_...", quantity: 2 },  // copy price IDs from Products
      { price: "price_...", quantity: 1 },
    ],
    order: "A-1043",            // your order number
    email: "buyer@example.com", // optional, for their receipt
  });

  // Optional: send the buyer to your thank-you page once they have paid.
  Shieldz.on("paid", () => { window.location.href = "/thank-you"; });
</script>`;

const SERVER_PAGE = `<script src="${WIDGET_SRC}" defer></script>
<button id="pay">Pay with crypto</button>
<script>
  document.getElementById("pay").onclick = async () => {
    const res = await fetch("/create-payment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderId: "A-1043" }),
    });
    const { id } = await res.json();
    Shieldz.open({ invoice: id });
  };
</script>`;

const SERVER_CREATE: Record<ServerLang, { label: string; lang: string; code: string }> = {
  node: {
    label: "Node.js",
    lang: "javascript",
    code: `// Your server. The secret API key never goes into the page.
app.post("/create-payment", express.json(), async (req, res) => {
  const order = await getOrder(req.body.orderId); // your own order lookup
  const r = await fetch("https://shieldz.cash/api/v1/invoices", {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${process.env.SHIELDZ_API_KEY}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      // Price IDs from Products, or your own prices with price_data.
      line_items: order.items.map((i) => ({
        price_data: { name: i.name, unit_amount_usd_cents: i.priceCents },
        quantity: i.quantity,
      })),
      client_reference_id: order.id,
      idempotency_key: order.id,
    }),
  });
  const invoice = await r.json();
  res.json({ id: invoice.id });
});`,
  },
  php: {
    label: "PHP",
    lang: "php",
    code: `<?php // create-payment.php. The secret API key never goes into the page.
$in = json_decode(file_get_contents('php://input'), true);
$order = get_order($in['orderId']); // your own order lookup

$items = array_map(fn ($i) => [
  'price_data' => ['name' => $i->name, 'unit_amount_usd_cents' => $i->price_cents],
  'quantity' => $i->quantity,
], $order->items);

$ch = curl_init('https://shieldz.cash/api/v1/invoices');
curl_setopt_array($ch, [
  CURLOPT_POST => true,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => [
    'Authorization: Bearer ' . getenv('SHIELDZ_API_KEY'),
    'Content-Type: application/json',
  ],
  CURLOPT_POSTFIELDS => json_encode([
    'line_items' => $items,
    'client_reference_id' => $order->id,
    'idempotency_key' => $order->id,
  ]),
]);
$invoice = json_decode(curl_exec($ch), true);

header('Content-Type: application/json');
echo json_encode(['id' => $invoice['id']]);`,
  },
  python: {
    label: "Python",
    lang: "python",
    code: `# Your server (Flask). The secret API key never goes into the page.
@app.post("/create-payment")
def create_payment():
    order = get_order(request.json["orderId"])  # your own order lookup
    r = requests.post(
        "https://shieldz.cash/api/v1/invoices",
        headers={"Authorization": f"Bearer {os.environ['SHIELDZ_API_KEY']}"},
        json={
            "line_items": [
                {"price_data": {"name": i.name, "unit_amount_usd_cents": i.price_cents},
                 "quantity": i.quantity}
                for i in order.items
            ],
            "client_reference_id": order.id,
            "idempotency_key": order.id,
        },
    )
    return {"id": r.json()["id"]}`,
  },
};

const STORE_WEBHOOK = `// Shieldz calls this the moment a payment is confirmed.
// Set its address under Webhooks. verifyShieldzWebhook is in the Quick start tab.
app.post("/shieldz-webhook", express.raw({ type: "application/json" }), (req, res) => {
  const ok = verifyShieldzWebhook(req.body.toString(), req.get("X-Shieldz-Signature"), process.env.SHIELDZ_WHSEC);
  if (!ok) return res.sendStatus(400);

  const event = JSON.parse(req.body.toString());
  if (event.type === "invoice.paid") {
    const paid = event.data.invoice;
    // line_items: what was bought, priced by Shieldz from your catalog.
    // shipping: the address, if your payment link asks for it.
    fulfil(paid.client_reference_id ?? paid.payment_link, paid.line_items, paid.shipping);
  }
  res.sendStatus(200);
});`;

function Seg<T extends string>({ value, options, onChange, label }: {
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="qs-modeseg wg-seg" role="tablist" aria-label={label}>
      {options.map(([v, l]) => (
        <button key={v} type="button" role="tab" aria-selected={value === v}
          className={`qs-modeseg-btn${value === v ? " active" : ""}`} onClick={() => onChange(v)}>{l}</button>
      ))}
    </div>
  );
}

/** The real widget.js, wired to a payment link, right in the dashboard. */
function BuyButtonPreview({ id }: { id: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadWidget()
      .then(() => { if (live && ref.current) window.Shieldz?.scan(ref.current); })
      .catch((e) => { if (live) setErr(String(e instanceof Error ? e.message : e)); });
    return () => { live = false; };
  }, [id]);
  if (err) return <p className="qs-note qs-err">The preview could not load ({err}). Your code still works on your site.</p>;
  return (
    <div className="wg-preview" ref={ref} key={id}>
      <button type="button" data-shieldz-link={id}>Buy now</button>
    </div>
  );
}

function StoreFlow({ onWoo }: { onWoo: () => void }) {
  const t = useT();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [links, setLinks] = useState<PaymentLink[] | null>(null);
  const [linkId, setLinkId] = useState<string>("");
  const [loadErr, setLoadErr] = useState<string | null>(null);
  // Step 1: add a product right here.
  const [pName, setPName] = useState("");
  const [pPrice, setPPrice] = useState("");
  const [pBusy, setPBusy] = useState(false);
  const [pErr, setPErr] = useState<string | null>(null);
  // Step 2: one click from a product to a link.
  const [fromProduct, setFromProduct] = useState("");
  const [lBusy, setLBusy] = useState(false);
  const [lErr, setLErr] = useState<string | null>(null);
  const [how, setHow] = useState<StoreHow>("button");
  const [lang, setLang] = useState<ServerLang>("node");
  // JavaScript cart: the shop key, its websites and the price-ID switch.
  const [key, setKey] = useState<CheckoutKey | null>(null);
  const [keyErr, setKeyErr] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [sites, setSites] = useState("");
  const [sitesBusy, setSitesBusy] = useState(false);
  const [sitesMsg, setSitesMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [cpBusy, setCpBusy] = useState(false);
  // Live store payments need the payout address confirmed once.
  const [payout, setPayout] = useState<{ chain: string; asset: string; address: string } | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const [p, l] = await Promise.all([listProducts(), listPaymentLinks()]);
      const sellable = p.data.filter((x) => x.active);
      setProducts(sellable);
      const active = l.data.filter((x) => x.active);
      setLinks(active);
      setLinkId((cur) => cur || active[0]?.id || "");
      setFromProduct((cur) => cur || sellable[0]?.id || "");
    } catch (e) {
      setLoadErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
    getMyMerchant()
      .then((m) => { if (m.exists && !m.setup_verified && m.default_settlement) setPayout(m.default_settlement); })
      .catch(() => {});
    getCheckoutKey()
      .then((k) => { setKey(k); setSites(k.allowed_origins.join("\n")); })
      .catch((e) => setKeyErr(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const addProduct = async () => {
    const cents = toCents(pPrice);
    if (!pName.trim()) { setPErr("Give the product a name."); return; }
    if (cents == null || cents < 100) { setPErr("Type a price of at least $1, like 49.99."); return; }
    setPErr(null);
    setPBusy(true);
    try {
      const p = await createProduct({ name: pName.trim(), default_price_data: { unit_amount_usd_cents: cents } });
      setPName("");
      setPPrice("");
      setFromProduct(p.id);
      await load();
    } catch (e) {
      setPErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPBusy(false);
    }
  };

  const makeLink = async () => {
    if (!fromProduct) return;
    setLErr(null);
    setLBusy(true);
    try {
      const l = await createPaymentLink({
        line_items: [{ product: fromProduct, quantity: 1, adjustable_quantity: { enabled: true, minimum: 1, maximum: 10 } }],
      });
      setLinkId(l.id);
      await load();
    } catch (e) {
      setLErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLBusy(false);
    }
  };

  const rotate = async () => {
    if (!confirm("Make a new shop key? The old one stops working right away, so paste the new code into your website after this.")) return;
    setRotating(true);
    try { setKey(await rotateCheckoutKey()); } catch (e) { setKeyErr(e instanceof ApiError ? e.message : String(e)); }
    finally { setRotating(false); }
  };

  const saveSites = async () => {
    setSitesBusy(true);
    setSitesMsg(null);
    try {
      const list = sites.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      const r = await setCheckoutOrigins(list);
      setSites(r.allowed_origins.join("\n"));
      setKey((k) => (k ? { ...k, allowed_origins: r.allowed_origins } : k));
      setSitesMsg({ ok: true, text: r.allowed_origins.length ? "Saved. Only these websites can use your key now." : "Saved. Any website can use your key." });
    } catch (e) {
      setSitesMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setSitesBusy(false);
    }
  };

  const toggleClientPrices = async (on: boolean) => {
    setCpBusy(true);
    try {
      await setClientPrices(on);
      setKey((k) => (k ? { ...k, client_prices: on } : k));
    } catch (e) {
      setKeyErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCpBusy(false);
    }
  };

  const confirmPayout = async () => {
    setVerifyBusy(true);
    setVerifyErr(null);
    try {
      await verifySettlementSetup();
      setPayout(null);
    } catch (e) {
      setVerifyErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setVerifyBusy(false);
    }
  };

  const pk = key?.publishable_key ?? "pk_live_...";
  const link = links?.find((l) => l.id === linkId) ?? null;

  return (
    <>
      <div className="qs-inline wg-woo">
        <p className="qs-note" style={{ marginTop: 0 }}>
          <strong>Using WooCommerce?</strong> Our plugin does all of this for you.{" "}
          <button type="button" className="qs-link wg-linkbtn" onClick={onWoo}>Open the WooCommerce guide</button>
        </p>
      </div>

      {payout && (
        <div className="qs-inline wg-verify">
          <div className="qs-inline-head"><Ic name="warn" size={15} /> One quick check first</div>
          <p className="qs-note" style={{ marginTop: 0 }}>
            Your store payments go to this wallet ({payout.asset.split(".").pop()} on {payout.chain}). Please
            make sure it is yours before buyers start paying:
          </p>
          <code className="wg-addr">{payout.address}</code>
          <div className="wg-form" style={{ marginTop: 10 }}>
            <Btn onClick={() => void confirmPayout()} loading={verifyBusy} disabled={verifyBusy}>
              <Ic name="check" size={15} /> Yes, that is my wallet
            </Btn>
            <Link to="/coins" className="qs-link">No, change it on the Coins page</Link>
          </div>
          {verifyErr && <p className="qs-note qs-err">{verifyErr}</p>}
        </div>
      )}

      {loadErr && <p className="qs-note qs-err">{loadErr}</p>}

      <Step n={1} title={t("qs.store.step1")}>
        <p className="qs-p">
          You set your prices here, in Shieldz. Your website only says <em>which</em> product, so a
          buyer can never change what they pay.
        </p>
        {products && products.length > 0 && (
          <ul className="wg-where">
            {products.slice(0, 5).map((p) => (
              <li key={p.id}><strong>{p.name}</strong> · {priceLabel(p.default_price)}</li>
            ))}
            {products.length > 5 && <li>and {products.length - 5} more</li>}
          </ul>
        )}
        <div className="wg-form">
          <label className="wg-field wg-grow">
            <span>Product</span>
            <input className="qs-input" value={pName} maxLength={250} placeholder="Blue hoodie" onChange={(e) => setPName(e.target.value)} />
          </label>
          <label className="wg-field">
            <span>Price in USD</span>
            <input className="qs-input" inputMode="decimal" value={pPrice} placeholder="49.99" onChange={(e) => setPPrice(e.target.value)} />
          </label>
          <Btn onClick={() => void addProduct()} loading={pBusy} disabled={pBusy}>
            <Ic name="plus" size={15} /> Add product
          </Btn>
        </div>
        {pErr && <p className="qs-note qs-err">{pErr}</p>}
        <p className="qs-note">
          Photos, descriptions and "customer chooses the amount" are on the{" "}
          <Link to="/products" className="qs-link">Products</Link> page.
        </p>
      </Step>

      <Step n={2} title={t("qs.store.step2")}>
        <p className="qs-p">
          A payment link is what the buy button opens: one or more products, and whether the buyer
          can change the quantity or must give an email or a shipping address.
        </p>
        {links && links.length > 0 && (
          <label className="wg-field wg-grow" style={{ marginBottom: 12 }}>
            <span>Use this link</span>
            <select className="qs-input" value={linkId} onChange={(e) => setLinkId(e.target.value)}>
              {links.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.line_items.map((li) => li.product?.name ?? "?").join(", ")} · {l.id}
                </option>
              ))}
            </select>
          </label>
        )}
        {products && products.length > 0 ? (
          <div className="wg-form">
            <label className="wg-field wg-grow">
              <span>{links && links.length > 0 ? "Or make a new one for" : "Make a link for"}</span>
              <select className="qs-input" value={fromProduct} onChange={(e) => setFromProduct(e.target.value)}>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {priceLabel(p.default_price)}</option>)}
              </select>
            </label>
            <Btn variant={links && links.length > 0 ? "secondary" : "primary"} onClick={() => void makeLink()} loading={lBusy} disabled={lBusy}>
              <Ic name="link" size={15} /> Make payment link
            </Btn>
          </div>
        ) : (
          <p className="qs-note">Add a product in step 1 first.</p>
        )}
        {lErr && <p className="qs-note qs-err">{lErr}</p>}
        <p className="qs-note">
          Several products, email or shipping address: set it up on{" "}
          <Link to="/links" className="qs-link">Payment links</Link>.
        </p>
      </Step>

      <Step n={3} title={t("qs.store.step3")}>
        <Seg label="How to add it" value={how} onChange={setHow}
          options={[["button", "Buy button"], ["js", "JavaScript cart"], ["server", "My own server"]]} />

        {how === "button" && (
          <>
            <p className="qs-p">
              Paste this where the product is. It turns into the Shieldz button and opens the checkout
              right on your page. No key, no code to change.
            </p>
            <Code code={buyButtonSnippet("https://shieldz.cash", linkId || "plink_...")} lang="html" />
            {link && (
              <p className="qs-note">
                Or just share the link itself, in a post, an email or a chat:{" "}
                <a className="qs-link" href={link.url} target="_blank" rel="noreferrer">{link.url}</a>
              </p>
            )}
          </>
        )}

        {how === "js" && (
          <>
            <p className="qs-p">
              Have your own cart? Your page can start a checkout from price IDs. The prices still come
              from Shieldz; the page only says what is in the cart and how many.
            </p>
            {keyErr && <p className="qs-note qs-err">{keyErr}</p>}
            <label className="wg-check">
              <input type="checkbox" checked={!!key?.client_prices} disabled={!key || cpBusy}
                onChange={(e) => void toggleClientPrices(e.target.checked)} />
              <span>Let my website start a checkout from price IDs</span>
            </label>
            <div className="qs-sub">Your shop key</div>
            <p className="qs-note" style={{ marginTop: 0 }}>
              Safe to put in your page: it can only start payments to you, for your own prices.
            </p>
            {key ? <Secret value={key.publishable_key} /> : !keyErr && <p className="qs-note">Loading your key…</p>}
            <div className="qs-sub">Only allow your website (recommended)</div>
            <p className="qs-note" style={{ marginTop: 0 }}>
              Type your shop's address, like <code>myshop.com</code>. One per line. Leave it empty to
              allow any website.
            </p>
            <div className="wg-sites">
              <textarea className="qs-input wg-textarea" rows={2} placeholder="myshop.com" value={sites}
                onChange={(e) => setSites(e.target.value)} />
              <Btn variant="secondary" onClick={() => void saveSites()} loading={sitesBusy} disabled={sitesBusy || !key}>Save</Btn>
            </div>
            {sitesMsg && <p className={`qs-note${sitesMsg.ok ? " wg-ok" : " qs-err"}`}>{sitesMsg.ok && <Ic name="check" size={13} />} {sitesMsg.text}</p>}
            <p className="qs-note">
              Think your key leaked?{" "}
              <button type="button" className="qs-link wg-linkbtn" onClick={() => void rotate()} disabled={rotating || !key}>
                Make a new key
              </button>
            </p>
            <div className="qs-sub">On your page</div>
            <Code code={STORE_JS(pk)} lang="html" />
          </>
        )}

        {how === "server" && (
          <>
            <p className="qs-p">
              Your server creates the payment with your secret API key (from{" "}
              <Link to="/api-keys" className="qs-link">API keys</Link>) and the list of what was bought.
              Your page then just opens it.
            </p>
            <div className="qs-sub">On your server</div>
            <Seg label="Server language" value={lang} onChange={setLang}
              options={(Object.keys(SERVER_CREATE) as ServerLang[]).map((l) => [l, SERVER_CREATE[l].label])} />
            <Code code={SERVER_CREATE[lang].code} lang={SERVER_CREATE[lang].lang} />
            <div className="qs-sub">On your checkout page</div>
            <Code code={SERVER_PAGE} lang="html" />
          </>
        )}
      </Step>

      <Step n={4} title={t("qs.store.step4")}>
        <p className="qs-p">
          This is the real button with your payment link, just like your buyers will see it. Nothing
          is charged unless you actually send crypto. A test shows up in Activity as unpaid and expires
          on its own.
        </p>
        {linkId ? <BuyButtonPreview id={linkId} /> : <p className="qs-note">Make a payment link in step 2 to try it.</p>}
      </Step>

      <Step n={5} title={t("qs.store.step5")}>
        <p className="qs-p">
          Every payment shows up in <Link to="/activity" className="qs-link">Activity</Link> with what
          was bought and, if you asked for it, where to ship. The money goes straight to your wallet.
        </p>
        <ul className="wg-where">
          <li><strong>No server?</strong> Ship the order once Activity says <em>Paid</em>.</li>
          <li>
            <strong>Have a server?</strong> Set up a <Link to="/webhooks" className="qs-link">webhook</Link>.
            We call it the moment a payment is confirmed, with the items and the address in it.
          </li>
        </ul>
        <Code code={STORE_WEBHOOK} lang="javascript" />
      </Step>
    </>
  );
}

type WidgetUse = "once" | "store";

function WidgetLane({ onWoo }: { onWoo: () => void }) {
  const [use, setUse] = useState<WidgetUse>("store");
  return (
    <div className="qs-lane">
      <p className="qs-p wg-lead">
        Add a Shieldz checkout to your own website. Your buyer pays in any coin without leaving your
        page, and the money goes straight to your wallet. First, what are you selling?
      </p>
      <div className="wg-looks wg-uses" role="radiogroup" aria-label="What are you selling?">
        <button type="button" role="radio" aria-checked={use === "store"}
          className={`wg-look${use === "store" ? " on" : ""}`} onClick={() => setUse("store")}>
          <Ic name="wallet" size={20} />
          <strong>An online store</strong>
          <span>Your products and prices, with a buy button on your site.</span>
        </button>
        <button type="button" role="radio" aria-checked={use === "once"}
          className={`wg-look${use === "once" ? " on" : ""}`} onClick={() => setUse("once")}>
          <Ic name="link" size={20} />
          <strong>One payment</strong>
          <span>An invoice, a client job or a single item. No code to write.</span>
        </button>
      </div>
      {use === "store" ? <StoreFlow onWoo={onWoo} /> : <OneTimeFlow />}
    </div>
  );
}
